import { useState } from "react";
import { ArrowLeft, Languages, Mail, Lock, Eye, EyeOff, ArrowRight, UserPlus, LogIn, Plane, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { BASE_URL, postWithoutTokenAPI } from "../util/Api";
import "../styles/LoginPage.css";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setUserId } from "../redux/userSlice";

import { ForgotPasswordModal } from "./ForgotPasswordModal";

export function LoginPage({ onNavigate }) {
  const [activeTab, setActiveTab] = useState("signin"); // "signin" | "signup"
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    mobile_number: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState("en");

  const changeLanguage = () => {
    const newLang = currentLang === "en" ? "ta" : "en";
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    }
  };

  const handleMobileChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData({ ...formData, mobile_number: digitsOnly });
    if (errors.mobile_number) {
      setErrors((prev) => ({ ...prev, mobile_number: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t("Email Address is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t("Please enter a valid email address");
    }

    if (!formData.password) {
      newErrors.password = t("Password is required");
    } else if (activeTab === "signup" && formData.password.length < 8) {
      newErrors.password = t("Password must be at least 8 characters long");
    }

    if (activeTab === "signup") {
      if (!formData.mobile_number.trim()) {
        newErrors.mobile_number = t("Mobile Number is required");
      } else if (!/^\d{10}$/.test(formData.mobile_number.trim())) {
        newErrors.mobile_number = t("Please enter a valid 10-digit mobile number");
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t("Please confirm your password");
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t("Passwords do not match");
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      const endpoint = activeTab === "signin" ? "/auth/login" : "/auth/signup";
      const payload = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      };

      if (activeTab === "signup") {
        payload.mobile_number = formData.mobile_number.trim();
      }

      const response = await postWithoutTokenAPI(endpoint, payload);

      if (response?.statusCode === 200 || response?.data?.accessToken) {
        const verifiedUser = response?.data;
        dispatch(setUserId(verifiedUser));
        localStorage.setItem(
          "UserId",
          JSON.stringify({
            id: verifiedUser,
            timestamp: new Date().toISOString(),
          })
        );
        toast.success(t(activeTab === "signin" ? "Login Successfully" : "Account Created Successfully"));
        if (!response?.data?.profile_completed) {
          navigate("/register", { state: { verifiedUser: response.data.accessToken } });
        } else {
          navigate("/");
        }
      } else {
        const msg = response?.message || t("Invalid credentials. Please try again.");
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.message || t("Something went wrong. Please try again");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header with Back & Language Switcher */}
      <div className="bg-white shadow-sm border-b border-border">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate("/")}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-medium">{t("Back")}</span>
            </button>

            <div className="w-20">
              <Button
                variant="ghost"
                size="sm"
                onClick={changeLanguage}
                className="avan-avala-language-btn hidden md:flex items-center space-x-1 text-gray-600 cursor-pointer hover:text-orange-500"
              >
                <Languages className="w-4 h-4" />
                <span className="avan-avala-language-text text-sm font-medium">
                  {currentLang === "en" ? "தமிழ்" : "English"}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Toaster position="top-right" />

      <div className="login-wrapper">
        {/* Left Image Section matching reference design */}
        <div className="login-left">
          <ImageWithFallback
            src="/Images/authentic-indian-bride-groom.jpg"
            alt="Couple"
            className="login-image"
          />
          <div className="login-overlay">
            <div className="email-login-badge inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-900/60 backdrop-blur-md border border-amber-500/40 text-amber-300 text-xs font-semibold mb-3">
              <Plane className="w-3.5 h-3.5 rotate-[-45deg]" />
              <span>EMAIL LOGIN</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold leading-tight mb-2">
              {t("Your Perfect Match Awaits You")}
            </h2>
            <p className="text-sm text-gray-200">
              {t("Join thousands of happy couples who found their life partner on AvanAval.")}
            </p>
          </div>
        </div>

        {/* Right Form Section */}
        <div className="login-right">
          <Card className="login-card w-full max-w-md">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-gray-900">
                {activeTab === "signin" ? t("Welcome Back") : t("Create an Account")}
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                {activeTab === "signin"
                  ? t("Sign in with your email and password")
                  : t("Sign up with your email and password")}
              </p>

              {/* Segmented Sign In / Sign Up Toggle */}
              <div className="flex bg-gray-100 p-1 rounded-xl mt-4 border border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("signin");
                    setErrors({});
                  }}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 transition-all ${activeTab === "signin"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                    }`}
                >
                  <LogIn className="w-4 h-4 text-red-500" />
                  <span>{t("Sign In")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("signup");
                    setErrors({});
                  }}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 transition-all ${activeTab === "signup"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                    }`}
                >
                  <UserPlus className="w-4 h-4 text-gray-500" />
                  <span>{t("Sign Up")}</span>
                </button>
              </div>
            </CardHeader>

            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="login-form space-y-4" noValidate>
                {/* Email Address */}
                <div className="form-group">
                  <Label htmlFor="email" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                    <Mail size={15} className="text-gray-500" />
                    {t("Email Address")}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="erpadmin@diusdesign.in"
                    value={formData.email}
                    onChange={handleChange}
                    className="login-input-field"
                    required
                  />
                  {errors.email && <span className="field-error-text">{errors.email}</span>}
                </div>

                {/* Mobile Number (Signup Only) */}
                {activeTab === "signup" && (
                  <div className="form-group">
                    <Label htmlFor="mobile_number" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                      <Phone size={15} className="text-gray-500" />
                      {t("Mobile Number")}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="mobile_number"
                      name="mobile_number"
                      type="tel"
                      inputMode="numeric"
                      placeholder="9876543210"
                      value={formData.mobile_number}
                      onChange={handleMobileChange}
                      maxLength={10}
                      className="login-input-field"
                      required
                    />
                    {errors.mobile_number && <span className="field-error-text">{errors.mobile_number}</span>}
                  </div>
                )}

                {/* Password */}
                <div className="form-group">
                  <Label htmlFor="password" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                    <Lock size={15} className="text-gray-500" />
                    {t("Password")}
                    <span className="text-red-500">*</span>
                  </Label>
                  <div className="password-input-wrapper">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="login-input-field"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-eye-btn"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && <span className="field-error-text">{errors.password}</span>}
                  {activeTab === "signup" && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={16} className={formData.password.length >= 8 ? "text-green-500" : "text-gray-300"} />
                        <span className={`text-xs ${formData.password.length >= 8 ? "text-gray-700 font-medium" : "text-gray-500"}`}>
                          {t("Minimum 8 characters")}
                        </span>
                      </div>
                    </div>
                  )}

                  {activeTab === "signin" && (
                    <div className="forgot-password-row">
                      <button
                        type="button"
                        onClick={() => setShowForgotPasswordModal(true)}
                        className="forgot-password-btn"
                      >
                        {t("Forgot Password?")}
                      </button>
                    </div>
                  )}
                </div>

                {/* Confirm Password (Signup Only) */}
                {activeTab === "signup" && (
                  <div className="form-group">
                    <Label htmlFor="confirmPassword" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                      <Lock size={15} className="text-gray-500" />
                      {t("Confirm Password")}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="password-input-wrapper">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="••••••••••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="login-input-field"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="password-eye-btn"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className="field-error-text">{errors.confirmPassword}</span>}
                  </div>
                )}

                {/* Action Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: '#dc2626' }}
                  className="w-full h-11 rounded-lg text-white font-semibold flex items-center justify-center gap-2 shadow-md cursor-pointer transition-all mt-2"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span>{loading ? t("Please wait...") : activeTab === "signin" ? t("Sign In") : t("Sign Up")}</span>
                </Button>

                {/* Switch tab footer link */}
                <div className="text-center pt-2">
                  {activeTab === "signin" ? (
                    <p className="text-xs text-gray-600">
                      {t("Don't have an account?")}{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab("signup");
                          setErrors({});
                        }}
                        className="text-red-600 font-semibold underline hover:text-red-700 cursor-pointer"
                      >
                        {t("Create one here")}
                      </button>
                    </p>
                  ) : (
                    <p className="text-xs text-gray-600">
                      {t("Already have an account?")}{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab("signin");
                          setErrors({});
                        }}
                        className="text-red-600 font-semibold underline hover:text-red-700 cursor-pointer"
                      >
                        {t("Sign in here")}
                      </button>
                    </p>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        t={t}
      />
    </>
  );
}